import { isCellsOnlyDeletion } from "@ext/git/core/Diff/logic/deletedColumns";
import type { ProseMirrorDiffLine } from "@ext/git/core/Diff/logic/model/ProseMirrorDiffLine";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { JSONContent } from "@tiptap/core";
import type { Node } from "prosemirror-model";

const boundsOf = (node: Node, pos: number) => ({ from: pos, to: pos + node.nodeSize - 1 });

// Table and list parts need their parents: a bare row crashes TableMap ("Not a table node") in the popover editor.
const TABLE_PART_PARENTS: Record<string, string[]> = {
	tableRow: ["table"],
	tableCell: ["table", "tableRow"],
	tableHeader: ["table", "tableRow"],
	listItem: ["bulletList"],
	taskItem: ["taskList"],
};

const standalone = (json: JSONContent): JSONContent => {
	const parents = TABLE_PART_PARENTS[json.type ?? ""];
	if (!parents) return json;
	return parents.reduceRight<JSONContent>((inner, type) => ({ type, content: [inner] }), json);
};

const wrapInDoc = (nodes: Node[]) => ({ type: "doc", content: nodes.map((node) => standalone(node.toJSON())) });

export type DiffDocs = { before: Node; after: Node };

const RESOURCE_NODES = new Set(["image", "drawio", "diagrams", "openapi", "video"]);

const itemAround = (doc: Node, pos: number): Node | null => {
	if (pos < 0 || pos > doc.content.size) return null;
	const Pos = doc.resolve(pos);
	for (let depth = Pos.depth; depth > 1; depth--) {
		if (Pos.node(depth).type.name !== "listItem") continue;
		const list = Pos.node(depth - 1);
		return list.type.create(list.attrs, Pos.node(depth));
	}
	return null;
};

const LIST_ITEMS = new Set(["listItem", "taskItem"]);

const inOwnList = (doc: Node, pos: number, node: Node): Node => {
	if (!LIST_ITEMS.has(node.type.name) || pos < 0 || pos > doc.content.size) return node;
	const list = doc.resolve(pos).parent;
	return list.type.create(list.attrs, node);
};

const popoverSides = (change: DiffChange, docs?: DiffDocs): { old: Node[]; current: Node[] } => {
	const plain = {
		old: change.nodeA
			? [docs && change.posA !== undefined ? inOwnList(docs.before, change.posA, change.nodeA) : change.nodeA]
			: [],
		current: change.nodeB
			? [docs && change.posB !== undefined ? inOwnList(docs.after, change.posB, change.nodeB) : change.nodeB]
			: [],
	};
	if (!docs || !change.nodeB || !RESOURCE_NODES.has(change.nodeB.type.name)) return plain;
	if (change.posA === undefined || change.posB === undefined) return plain;

	const oldItem = itemAround(docs.before, change.posA);
	const newItem = itemAround(docs.after, change.posB);
	if (!oldItem || !newItem) return plain;
	return { old: [oldItem], current: [newItem] };
};

const toDiffLines = (changes: DiffChange[], docs?: DiffDocs): ProseMirrorDiffLine[] => {
	const lines: ProseMirrorDiffLine[] = [];

	changes.forEach((change) => {
		// Deletions are drawn by the collapse plugin; a margin bar here would duplicate the marker.
		if (change.type === "deleted") return;
		// The deleted column draws its own marker; a bar on every row would repeat it.
		if (isCellsOnlyDeletion(change)) return;

		if (!change.nodeB || change.posB === undefined) return;

		const pos = boundsOf(change.nodeB, change.posB);

		if (change.type === "inserted") {
			lines.push({ type: "added", pos });
			return;
		}

		if (change.type === "moved") return;

		const sides = popoverSides(change, docs);
		lines.push({
			type: "modified",
			pos,
			oldPos: change.nodeA && change.posA !== undefined ? boundsOf(change.nodeA, change.posA) : pos,
			oldNode: change.nodeA,
			oldContent: wrapInDoc(sides.old),
			newContent: wrapInDoc(sides.current),
		});
	});

	return lines;
};

export default toDiffLines;
